import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

const id = publicIdSchema;
export const visitContentQuerySchema = z.strictObject({
  limit: z
    .string()
    .regex(/^[1-9][0-9]*$/)
    .transform(Number)
    .pipe(z.int().positive().max(100))
    .default(50),
  cursor: id.optional(),
});
export type VisitContentQuery = z.output<typeof visitContentQuerySchema>;
export const visitRecordingSchema = z.object({
  public_id: publicIdSchema,
  visitPublicId: publicIdSchema,
  name: z.string().nullable(),
  transcription: z.string().nullable(),
  status: z.string().nullable(),
  durationSeconds: z.number().nonnegative().nullable(),
  createdAt: z.string().nullable(),
});
export const visitNoteSchema = z.object({
  public_id: publicIdSchema,
  visitPublicId: publicIdSchema,
  template: z.string().nullable(),
  format: z.string().nullable(),
  style: z.string().nullable(),
  exam: z.string().nullable(),
  result: z.string().nullable(),
  original: z.string().nullable(),
  macroPublicIds: z.array(publicIdSchema).nullable(),
  recordingPublicIds: z.array(publicIdSchema).nullable(),
  customTemplatePublicId: publicIdSchema.nullable(),
  customTemplateName: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export const visitVersionSchema = z.object({
  public_id: publicIdSchema,
  visitPublicId: publicIdSchema,
  transcription: z.string().nullable(),
  result: z.string().nullable(),
  createdAt: z.iso.datetime(),
  version: z.int(),
});
const nextCursor = publicIdSchema.nullable();
export const visitRecordingsSchema = z.object({
  items: z.array(visitRecordingSchema),
  nextCursor,
});
export const visitNotesSchema = z.object({
  items: z.array(visitNoteSchema),
  nextCursor,
});
const read = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  NOT_FOUND: { message: "Not Found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
const collectionInput = z.object({
  params: z.strictObject({ public_id: id }),
  query: visitContentQuerySchema.default({ limit: 50 }),
  body: z.undefined().optional(),
});
export const visitContentContract = {
  listVisitRecordings: read
    .input(collectionInput)
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits/{public_id}/recordings",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitRecordingsSchema),
  listVisitNotes: read
    .input(collectionInput)
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits/{public_id}/notes",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitNotesSchema),
  getVisitVersion: read
    .input(
      z.object({
        params: z.strictObject({ public_id: id, versionPublicId: id }),
        query: z.strictObject({}).default({}),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/visits/{public_id}/versions/{versionPublicId}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitVersionSchema),
};
